import React, { useEffect, useRef, useState } from "react";
import { Button, DeviceType, useDeviceType } from "@geotab/zenith";
import type AppView from "../../../../../../app.view";
import * as logger from "../../../../../../utils/logger";
import type { IApiError } from "../../../../../../utils/api.interfaces";
import { isOverLimitException } from "../../../../../../utils/errorHandler/error.types";
import { FeatureFlagName } from "../../../../../../utils/featureFlagName";
import LoginTranslations from "../../../helpers/translations";
import { getPasswordRules, IPasswordPolicy } from "../../../helpers/passwordRules";
import { loadCommonPasswords } from "../../../helpers/commonPasswords";
import { signInWithNewPassword } from "../../../services/signInWithNewPassword";
import { renderUntilAnswered, TTranslate } from "../../../helpers/renderUntilAnswered";
import PasswordField from "./PasswordField";
import PasswordPolicyList from "./PasswordPolicyList";
import PasswordUpdatedModal from "./PasswordUpdatedModal";

// like MyGeotab, any other server message is shown as a generic failure
const SERVER_ERRORS: string[] = [
    LoginTranslations.CommonPasswordsNotAllowed,
    LoginTranslations.PasswordTooWeak,
    LoginTranslations.PasswordRecentlyUsed,
    LoginTranslations.PasswordContainsUserName,
    LoginTranslations.PasswordDoesNotMeetPolicy
];

/** Like MyGeotab, the updated page shows the existing message for a rule the server rejects in its own words. */
const getUpdatedPageMessage = (message = ""): string | undefined => {
    if (message.includes("Cannot reuse password")) {
        return LoginTranslations.PasswordRecentlyUsed;
    }
    if (message.includes("The password cannot contain the username")) {
        return LoginTranslations.PasswordContainsUserName;
    }
    if (message.includes("Password policy requires")) {
        return LoginTranslations.PasswordDoesNotMeetPolicy;
    }
    return undefined;
};

const getServerError = (error: IApiError, isUpdatedPageEnabled: boolean): string => {
    // the server takes two password changes a minute
    if (isOverLimitException(error)) {
        return LoginTranslations.TooManyRequests;
    }
    const message = (isUpdatedPageEnabled && getUpdatedPageMessage(error?.message)) || error?.message;
    return SERVER_ERRORS.indexOf(message) !== -1 ? message : LoginTranslations.RequestNotCompleted;
};

interface IUpdatePasswordPageProps {
    app: AppView;
    policy: IPasswordPolicy;
    translate: TTranslate;
    onFinished: () => void;
}

/** A copy of the MyGeotab password update page. It has no way out other than updating the password, or the session ending. */
const UpdatePasswordPage: React.FC<IUpdatePasswordPageProps> = ({ app, policy, translate, onFinished }: IUpdatePasswordPageProps) => {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [serverError, setServerError] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
    const isUpdatedPageEnabled = app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_UPDATED_PASSWORD_PAGE);
    // only the updated page checks the common password rule before submitting
    const [commonPasswords, setCommonPasswords] = useState<ReadonlySet<string>>();
    // like MyGeotab, nothing is flagged until the driver starts typing
    const [isTyping, setIsTyping] = useState(false);
    const [deviceType, setDeviceType] = useState<DeviceType>();
    useDeviceType(setDeviceType);
    const textClassName = `zen-form-field__text zen-form-field__text--${ deviceType === DeviceType.Mobile ? "drive" : "drive-tablet" }`;
    // the driver logging in, who is not the current user when adding a co-driver
    const user = app.api.getUser();
    const rules = getPasswordRules(policy, [user?.name, user?.firstName, user?.lastName], commonPasswords);
    // a rule that cannot be checked yet is left to the server
    const isAccepted = rules.every(rule => rule.isMet(password) !== false);
    const canSubmit = !isSubmitting && isAccepted && password === confirmPassword;
    // the drive top bar stays visible so the page carries its branding
    const top = document.getElementById("topbar-container")?.getBoundingClientRect().bottom ?? 0;
    // focus starts on the page, without opening the keyboard on a field
    const dialog = useRef<HTMLDivElement>(null);
    useEffect(() => dialog.current?.focus(), []);
    useEffect(() => {
        if (!isUpdatedPageEnabled) {
            return;
        }
        loadCommonPasswords(app.api)
            .then(setCommonPasswords)
            // the rule stays unmarked, and the server still rejects a common password on submit
            .catch(() => undefined);
    }, [app, isUpdatedPageEnabled]);

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        // taken before the change, which ends this session
        const session = app.api.getCurrentCredentials();
        if (!session) {
            // the session already ended, so login asks for credentials again
            onFinished();
            return;
        }
        if (!canSubmit) {
            return;
        }

        setIsSubmitting(true);
        setServerError(undefined);
        try {
            await app.api.callPromise("SetUserPassword", { newPassword: password });
        } catch (error) {
            // an ended session clears the credentials, so trying again could never work
            if (!app.api.getCurrentCredentials()) {
                onFinished();
                return;
            }
            setServerError(getServerError(error, isUpdatedPageEnabled));
            setIsSubmitting(false);
            return;
        }

        try {
            await signInWithNewPassword(app.api, session, password, true);
        } catch (error) {
            // the change ended this session, so login asks for the new password
            logger.error(logger.Source.USER, `Sign in after password update failed: ${ error?.message }`, user);
        }
        if (isUpdatedPageEnabled) {
            // the form stays disabled behind the confirmation
            setIsPasswordUpdated(true);
            return;
        }
        onFinished();
    };

    return (
        <div ref={dialog} className="update-password" style={{ top }} role="dialog" aria-modal="true" aria-labelledby="update-password-header" tabIndex={-1}>
            {/* older webviews ignore inert, so this invisible cover stops the top bar buttons being tapped */}
            <div className="update-password__shield" style={{ height: top }} />
            <form className="update-password__page" onSubmit={event => void submit(event)}>
                <h1 id="update-password-header" className="update-password__header">{translate(LoginTranslations.UpdateYourPassword)}</h1>
                <div className="update-password__group">
                    <PasswordField
                        id="update-password-new"
                        label={translate(isUpdatedPageEnabled ? LoginTranslations.NewPassword : LoginTranslations.Password)}
                        value={password}
                        error={serverError && translate(serverError)}
                        disabled={isSubmitting}
                        textClassName={textClassName}
                        translate={translate}
                        onChange={value => {
                            setPassword(value);
                            setIsTyping(true);
                            setServerError(undefined);
                        }}
                    />
                    <PasswordPolicyList rules={rules} password={password} isAccepted={isAccepted} isTyping={isTyping} isUpdatedPageEnabled={isUpdatedPageEnabled} textClassName={textClassName} translate={translate} />
                </div>
                <div className="update-password__group">
                    <PasswordField
                        id="update-password-confirm"
                        label={translate(LoginTranslations.ConfirmPassword)}
                        value={confirmPassword}
                        error={password && confirmPassword && confirmPassword !== password ? translate(LoginTranslations.PasswordsDoNotMatch) : undefined}
                        disabled={isSubmitting}
                        textClassName={textClassName}
                        translate={translate}
                        onChange={value => {
                            setConfirmPassword(value);
                            setServerError(undefined);
                        }}
                    />
                </div>
                <Button id="update-password-submit" className="update-password__submit login-primary-button" type="primary" htmlType="submit" disabled={!canSubmit}>
                    {translate(LoginTranslations.UpdatePasswordButton)}
                </Button>
            </form>
            {isPasswordUpdated && <PasswordUpdatedModal translate={translate} onContinue={onFinished} />}
        </div>
    );
};

/** Resolves once the password is updated or the session ends. */
export function showUpdatePasswordPage(app: AppView, policy: IPasswordPolicy): Promise<void> {
    return renderUntilAnswered<void>(app, "Password update page", undefined, (answer, translate) => (
        <UpdatePasswordPage app={app} policy={policy} translate={translate} onFinished={answer} />
    ));
}

export default UpdatePasswordPage;
